import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import numberDecorator from 'src/decorators/number.decorator';
import { OperationEventEntity } from 'src/entities/OperationEvent.entity';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';

type PlaceInfo = {
  shipperName: string | null;
  placeName: string | null;
  placeAddress: string | null;
  timeRange: string | null;
  waitTime: string | null;
  workTime: string | null;
  count: string | null;
  weight: string | null;
  volume: string | null;
  constraints: string | null;
  overtime: boolean;
  weightOver: boolean;
  volumeOver: boolean;
  outsideSpecifiedTime: boolean;
  hasWarning: boolean;
};

type OperationInfoPanelProps = {
  open: boolean;
  operation: OperationEventEntity | null;
  deliveryData: PlanningsDeliveryEntity[];
  truckData: PlanningsTruckEntity[];
  driverEntities: PlanningsDriverEntity[];
  onClose: () => void;
  anchorEl: HTMLElement | null;
  eventDialogSelectedOrderIds: number[];
  addEventDialogSelectedOrderIds: (orderId: number) => void;
  deleteButtonOnClick: () => void;
  fixedShiftTruckIds: number[];
};

const OperationInfoPanel: FC<OperationInfoPanelProps> = memo(({
  open,
  operation,
  deliveryData,
  truckData,
  driverEntities,
  onClose,
  anchorEl,
  eventDialogSelectedOrderIds,
  addEventDialogSelectedOrderIds,
  deleteButtonOnClick,
  fixedShiftTruckIds,
}) => {
  const licenseContext = useContext(LicenseContext);
  const [unit, setUnit] = useState('');
  const [isDeliveryFixed, setIsDeliveryFixed] = useState(false);
  const [fixedOperationIds, setFixedOperationIds] = useState<number[]>([]);

  useEffect(() => {
    setUnit(licenseContext.config?.unit);
  }, [licenseContext]);

  useEffect(() => {
    setIsDeliveryFixed(fixedShiftTruckIds.includes(operation?.deliveryId));
  }, [fixedShiftTruckIds, operation]);

  useEffect(() => {
    if (!operation) return;

    const fixedOperations = operation.planningsOperations.filter((it) => it.fixed);
    setFixedOperationIds(fixedOperations.map((it) => it.id));
  }, [operation]);

  const [showDetails, setShowDetails] = useState(false);
  const panelWidth = 600; // パネルの幅
  const delivery: PlanningsDeliveryEntity = useMemo(() => {
    if (!operation || !deliveryData) return null;

    return deliveryData.find((it) => it.id === operation.deliveryId);
  }, [deliveryData, operation]);

  const truck: PlanningsTruckEntity = useMemo(() => {
    if (!delivery || !truckData) return null;

    return truckData.find((it) => it.id === delivery.truckId);
  }, [delivery, truckData]);

  const driver: PlanningsDriverEntity = useMemo(() => {
    if (!delivery || !driverEntities) return null;

    return driverEntities.find((it) => it.id === delivery.driverId);
  }, [delivery, driverEntities]);

  const placeInfo: PlaceInfo = useMemo(() => {
    if (!operation) return null;

    const { action, order } = operation.operations[0];

    if (!order) return null;

    const shipperName = ['荷主', order.shipper_name].join(':');
    const placeName = ['地点名', action === '積' ? order.loading_name : order.unloading_name].join(':');
    const placeAddress = ['地点住所', action === '積' ? order.loading_address : order.unloading_address].join(':');
    const timeRange = action === '積' ? [
      datetimeDecorator.toYyyyMmDdHhMm(new Date(order.loading_start_at), '/'),
      datetimeDecorator.toYyyyMmDdHhMm(new Date(order.loading_end_at), '/')
    ].join(' - ') : [
      datetimeDecorator.toYyyyMmDdHhMm(new Date(order.unloading_start_at), '/'),
      datetimeDecorator.toYyyyMmDdHhMm(new Date(order.unloading_end_at), '/')
    ].join(' - ');
    const waitSeconds = Math.max(...operation.planningsOperations.map((it) => it.waitingDurationSeconds));
    const waitTime = ['待機時間', (waitSeconds && waitSeconds > 0) ? numberDecorator.convertSecondsToMinutes(waitSeconds) : '0分'].join(':');
    const workSeconds = Math.max(...operation.planningsOperations.map((it) => it.operationDurationSeconds));
    const workTime = ['作業時間', (workSeconds && workSeconds > 0) ? numberDecorator.convertSecondsToMinutes(workSeconds) : '0分'].join(':');
    const countSum = operation.planningsOperations.reduce((acc, it) => (acc + it.itemCount), 0);
    const count = ['数量', (countSum && countSum > 0) ? String(countSum) : '-'].join(':');
    const weightSum = operation.planningsOperations.reduce((acc, it) => (acc + it.itemTotalWeightForCalculation), 0);
    const weight = ['重量', (weightSum && weightSum > 0) ? numberDecorator.convertGramToKg(weightSum, 1, true) : '-'].join(':');
    const volumeSum = operation.planningsOperations.reduce((acc, it) => (acc + (it.itemTotalVolumeMm3 || 0)), 0);
    const volume = ['体積', (volumeSum && volumeSum > 0) ? numberDecorator.convertMm3ToM3(volumeSum, 1, true) : '-'].join(':');
    const constraintsArray = [];
    if (!order.item_can_be_mixed) {
      constraintsArray.push('混載不可');
    }
    if (order.designated_truck_car_models && order.designated_truck_car_models.length > 0) {
      constraintsArray.push(order.designated_truck_car_models.join(','));
    }
    if (order.designated_truck_features && order.designated_truck_features.length > 0) {
      constraintsArray.push(order.designated_truck_features.join(','));
    }
    if (order.designated_truck_floor_specifications && order.designated_truck_floor_specifications.length > 0) {
      constraintsArray.push(order.designated_truck_floor_specifications.join(','));
    }
    if (order.designated_truck_klasses && order.designated_truck_klasses.length > 0) {
      constraintsArray.push(order.designated_truck_klasses.join(','));
    }
    if (order.designated_truck_loading_platform_heights && order.designated_truck_loading_platform_heights.length > 0) {
      constraintsArray.push(order.designated_truck_loading_platform_heights.join(','));
    }
    if (order.designated_truck_loading_platform_lengths && order.designated_truck_loading_platform_lengths.length > 0) {
      constraintsArray.push(order.designated_truck_loading_platform_lengths.join(','));
    }
    if (order.designated_truck_loading_platform_widths && order.designated_truck_loading_platform_widths.length > 0) {
      constraintsArray.push(order.designated_truck_loading_platform_widths.join(','));
    }
    const constraints = ['制約条件', constraintsArray.length > 0 ? constraintsArray.join(' | ') : '-'].join(':');
    const { overtime, weightOver, volumeOver, outsideSpecifiedTime, hasWarning } = operation;

    return {
      shipperName,
      placeName,
      placeAddress,
      timeRange,
      waitTime,
      workTime,
      count,
      weight,
      volume,
      constraints,
      overtime,
      weightOver,
      volumeOver,
      outsideSpecifiedTime,
      hasWarning,
    };
  }, [operation]);

  useEffect(() => {
    setShowDetails(false);
  }, [open]);

  // 位置の計算
  const position = useMemo(() => {
    if (!operation || !anchorEl) return { left: 0, top: 0 };

    const rect = anchorEl.getBoundingClientRect();
    const margin = 8; // マージン
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    // ダイアログの予想される高さ（showDetailsの状態に応じて）
    const estimatedHeight = showDetails ? Math.max(windowHeight * 0.8, 600) : 400;
    const estimateWidth = showDetails ? windowWidth * 0.6 : panelWidth;

    // 縦方向の位置調整
    let { top } = rect;
    // 下側にはみ出す場合
    if ((top + estimatedHeight + margin) > windowHeight) {
      top = windowHeight - estimatedHeight - margin;
    }

    let left = rect.left - panelWidth - margin;
    if (left < margin) { left = margin; }
    if ((left + estimateWidth) > windowWidth) {
      left = windowWidth - estimateWidth - margin;
    }

    return { left, top, };
  }, [operation, anchorEl, showDetails]);

  useEffect(() => {
    if (!open) return;
    if (!operation) return;

    operation.operations.forEach((it) => {
      if (it.order) {
        addEventDialogSelectedOrderIds(it.order.id);
      }
    });
  }, [operation, open]);

  const onClickDelete = useCallback(() => {
    onClose();
    deleteButtonOnClick();
  }, [deleteButtonOnClick, onClose]);

  const weightUnit = useCallback((val: number | undefined) => {
    if (val && val > 0) {
      const v = Math.ceil(val * 100) / 100;
      return [v, unit].join('');
    }
    return '-';
  }, [unit]);

  const volumeUnit = useCallback((val: number | undefined) => {
    if (val && val > 0) {
      const v = Math.ceil(val * 100) / 100;
      return [v, 'm3'].join('');
    }
    return '-';
  }, []);

  if (!operation || !anchorEl) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      hideBackdrop
      sx={{
        '& .MuiDialog-container': {
          display: 'block'
        },
        '& .MuiDialog-paper': {
          position: 'fixed',
          left: position.left,
          top: position.top,
          m: 0,
          width: showDetails ? '60vw' : panelWidth,
          minWidth: showDetails ? '60vw' : panelWidth,
          maxHeight: '80vh',
          overflowY: 'auto',
          transition: 'all 0.3s ease-in-out'
        }
      }}
    >
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">配送情報</Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                borderRadius: '50%',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {(isDeliveryFixed || fixedOperationIds.length === operation.operations.length) && (<Chip color="info" size="small" label="確定" />)}
              {!isDeliveryFixed && fixedOperationIds.length > 0 && fixedOperationIds.length < operation.operations.length && (<Chip color="info" size="small" label="一部確定" />)}
              <Typography variant="subtitle1" fontWeight="bold">
                {operation.title}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccessTimeIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                {`${datetimeDecorator.toYyyyMmDdHhMm(new Date(operation.start), '/')} - ${datetimeDecorator.toYyyyMmDdHhMm(new Date(operation.end), '/')}`}
              </Typography>
            </Stack>
            {placeInfo && placeInfo.hasWarning && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Stack>
                  <WarningAmberRoundedIcon color="error" />
                </Stack>
                {[
                  placeInfo.overtime ? '勤務時間が超過しています。' : '',
                  placeInfo.weightOver ? '重量が超過しています。' : '',
                  placeInfo.volumeOver ? '容量が超過しています。' : '',
                  placeInfo.outsideSpecifiedTime ? '指定時間が超過しています。' : '',
                ].join(' ')}
              </Stack>
            )}
            <Stack direction="row" alignItems="center" spacing={1}>
              {
                [truck.temporary ? '指定なし' : truck.licensePlateValue, driver.temporary ? '指定なし' : driver.name].join('／')
              }
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              {
                placeInfo && (
                  [placeInfo.shipperName, placeInfo.placeName, placeInfo.placeAddress, placeInfo.timeRange, placeInfo.waitTime, placeInfo.workTime,
                  placeInfo.count, placeInfo.weight, placeInfo.volume, placeInfo.constraints,
                  ].filter((it) => !!it).join('／')
                )
              }
            </Stack>
            <Stack direction="row" sx={{ alignItems: 'center', mt: 2, }}>
              <IconButton
                aria-label="expand row"
                onClick={() => setShowDetails((prev) => !prev)}
              >
                {showDetails ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
              <Typography onClick={() => setShowDetails((prev) => !prev)}>詳細</Typography>
            </Stack>
            <Stack>
              <Collapse in={showDetails} unmountOnExit>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>割当解除</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>注文番号</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>荷主名</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>地点名</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>住所</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>数量</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>重量</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>体積</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>品名</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>備考</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {operation.operations.filter((it) => it.order).map((it) => (
                      <TableRow key={['tableRow', it.operationId, it.action].join('-')}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Checkbox
                              size="small"
                              checked={!fixedOperationIds.includes(it.operationId) && eventDialogSelectedOrderIds.includes(it.order.id)}
                              onChange={() => addEventDialogSelectedOrderIds(it.order.id)}
                              disabled={fixedOperationIds.includes(it.operationId)}
                            />
                            {fixedOperationIds.includes(it.operationId) && (
                              <Chip label="確定" size="small" color="info" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{it.order.code}</TableCell>
                        <TableCell>{it.order.shipper_name}</TableCell>
                        <TableCell>
                          <Typography variant={it.action === '積' ? 'body1' : 'caption'}>{it.order.loading_name}</Typography>
                          <Typography variant="body2">=&gt;</Typography>
                          <Typography variant={it.action === '降' ? 'body1' : 'caption'}>{it.order.unloading_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant={it.action === '積' ? 'body1' : 'caption'}>{it.order.loading_address}</Typography>
                          <Typography variant="body2">=&gt;</Typography>
                          <Typography variant={it.action === '降' ? 'body1' : 'caption'}>{it.order.unloading_address}</Typography>
                        </TableCell>
                        <TableCell>{it.order.item_count}</TableCell>
                        <TableCell>{weightUnit(it.order.item_total_weight_kg)}</TableCell>
                        <TableCell>{volumeUnit(it.order.item_total_volume_m3)}</TableCell>
                        <TableCell>{it.order.item_name}</TableCell>
                        <TableCell>{it.order.memo}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Collapse>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onClickDelete}
          disabled={!eventDialogSelectedOrderIds.length || isDeliveryFixed}
        >
          割当解除
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default OperationInfoPanel;
